import {Checkbox, DatePicker, Form, Input, Select, Typography} from "@ui";
import moment from "moment";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {PrimaryButton} from "@/components/Button";
import {PERSISTENT_COOKIE_OPTIONS, setStorage} from "@/store/util.store";
import {getMetaPixelParams} from "@/util/urlParams";
import eyeActive from "../../assets/images/icon-eye-active.svg";
import eyeInactive from "../../assets/images/icon-eye-inactive.svg";
import useUserRegistration from "../../hooks/useUserRegistration";
import {useAuthStore} from "../../store/useAuth.store";
import {useLogo} from "../../util/logoUtils";
import styles from "./MobileRegistration.module.css";

const MobileRegistration = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const logo = useLogo();
  const [form] = Form.useForm();
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [registerButtonClicked, setRegisterButtonClicked] = useState(false);

  const authStatus = useAuthStore(state => state.authStatus);
  const validateSession = useAuthStore(state => state.validateSession);
  const _handleAuthForm = useAuthStore(state => state.handleAuthForm);

  const {mutate: postUserRegistration, isPending, error} = useUserRegistration();

  useEffect(() => {
    validateSession();
    setStorage("isMobileLoginUser", "true", PERSISTENT_COOKIE_OPTIONS);
  }, [validateSession]);

  useEffect(() => {
    if (authStatus === "authenticated") {
      navigate("/", {replace: true});
    }
  }, [authStatus, navigate]);

  const onFinish = values => {
    const birthDate = moment(values.birthDate.toDate());
    const age = moment().diff(birthDate, "years");
    setRegisterButtonClicked(true);

    if (age < 18) {
      form.setFields([{name: "birthDate", errors: [t("registrationBirthdate_message")]}]);
      return;
    }

    const formattedDate = values.birthDate.toISOString().split("T")[0];

    const {fbp, fbc} = getMetaPixelParams();

    const sendData = {
      birth_date: formattedDate,
      email: values.email?.trim().toLowerCase() ?? values.email,
      password: values.password,
      preferred_bonus: values.preferred_bonus || "slot",
    };

    if (fbp) {
      sendData.fbp = fbp;
    }
    if (fbc) {
      sendData.fbc = fbc;
    }

    setStorage("isMobileLoginUser", "true");

    postUserRegistration(sendData);
  };

  const onFinishFailed = _errorInfo => {};

  const isValidEmail = email => {
    if (!email) {
      return {valid: false, message: t("registrationEmail_required")};
    }
    const specialCharRegex = /[´ñáéíóúáèìòùâêîôûäëïöüãõ¨';[(){}¡!"ªº·#$%^&*+=\\|~<>¿?]/;
    if (specialCharRegex.test(email)) {
      return {valid: false, message: t("registrationEmail_specialChar")};
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
    if (!emailRegex.test(email)) {
      return {valid: false, message: t("registrationEmail_message")};
    }
    const [localPart, domainPart] = email.split("@");
    if (
      localPart.startsWith(".") ||
      localPart.endsWith(".") ||
      localPart.startsWith("-") ||
      localPart.startsWith("+") ||
      localPart.endsWith("-") ||
      localPart.endsWith("+")
    ) {
      return {valid: false, message: t("registrationEmail_CharStartEnd")};
    }
    if (
      localPart.includes("..") ||
      domainPart.includes("..") ||
      domainPart.includes("--") ||
      localPart.includes("--")
    ) {
      return {valid: false, message: t("registrationEmail_CharConsecutive")};
    }
    const domainParts = domainPart.split(".");
    for (const part of domainParts) {
      if (part.startsWith("-") || part.endsWith("-")) {
        return {valid: false, message: t("registrationEmail_CharStartEnd")};
      }
    }
    return {valid: true, message: ""};
  };

  if (authStatus === "checking") {
    return (
      <div className={styles.loadingContainer}>
        <img
          src={logo}
          alt="logo"
          className={styles.loadingLogo}
        />
        <div className={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.mobileRegistrationContainer}>
      <div className={styles.mobileRegistrationContent}>
        <div className={styles.logoContainer}>
          <img
            src={logo}
            alt="logo"
            className={styles.logo}
          />
        </div>

        <div className={styles.formContainer}>
          <Form
            form={form}
            name="mobile-registration"
            className={styles.form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            layout="vertical"
          >
            <div className={styles.formInner}>
              <h3 className={styles.title}>{t("baseModalTitleRegistration")}</h3>

              <Form.Item
                name="email"
                normalize={v => (typeof v === "string" ? v.trim() : v)}
                rules={[
                  {required: true, message: t("registrationEmail_required")},
                  {
                    validator: (_, value) => {
                      if (value) {
                        const result = isValidEmail(value);
                        if (!result.valid) {
                          return Promise.reject(new Error(result.message));
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                style={{width: "100%", marginBottom: "15px"}}
              >
                <Input
                  placeholder={t("registrationEmail")}
                  className={styles.input}
                />
              </Form.Item>

              <Form.Item
                name="birthDate"
                rules={[
                  {required: true, message: t("registrationSelectDate")},
                  () => ({
                    validator(_, value) {
                      if (!value) return Promise.resolve();
                      const birthDate = value?.toDate();
                      const age = moment().diff(moment(birthDate), "years");
                      if (age < 18) {
                        return Promise.reject(t("registrationBirthdate_message"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                style={{width: "100%", marginBottom: "15px"}}
              >
                <DatePicker
                  format="YYYY-MM-DD"
                  placeholder={t("registrationSelectDate")}
                  className={styles.input}
                  suffixIcon={null}
                  style={{width: "100%"}}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  {required: true, message: t("registrationPassword")},
                  {min: 8, message: t("registrationPasswordMinLength")},
                ]}
                style={{width: "100%", marginBottom: "15px"}}
              >
                <Input.Password
                  placeholder={t("registrationPassword")}
                  className={styles.input}
                  iconRender={visible => (
                    <img
                      src={visible ? eyeActive : eyeInactive}
                      alt="toggle password"
                      style={{width: 24, height: 24}}
                    />
                  )}
                />
              </Form.Item>

              <Form.Item
                name="repeat_password"
                dependencies={["password"]}
                rules={[
                  {
                    required: true,
                    message: t("registrationRepeatPassword_required"),
                  },
                  ({getFieldValue}) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error(t("registrationPasswordNotMatch")));
                    },
                  }),
                ]}
                style={{width: "100%", marginBottom: "15px"}}
              >
                <Input.Password
                  placeholder={t("registrationRepeatPassword")}
                  className={styles.input}
                  iconRender={visible => (
                    <img
                      src={visible ? eyeActive : eyeInactive}
                      alt="toggle password"
                      style={{width: 24, height: 24}}
                    />
                  )}
                />
              </Form.Item>

              <Form.Item
                name="preferred_bonus"
                style={{width: "100%", marginBottom: "15px"}}
              >
                <Select
                  className={styles.select}
                  placeholder={t("registrationBonusTitle")}
                  popupMatchSelectWidth={false}
                  style={{width: "100%"}}
                >
                  <Select.Option value="slot">{t("registrationFormBonusSlot")}</Select.Option>
                  <Select.Option value="casino_live">{t("registrationFormBonusCasino")}</Select.Option>
                  <Select.Option value="parley">{t("registrationFormBonusParley")}</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="terms"
                valuePropName="checked"
                rules={[{required: true, message: t("registrationAcceptTerms")}]}
                style={{width: "100%", marginBottom: "20px"}}
              >
                <Checkbox onChange={e => setAgreeToTerms(e.target.checked)}>
                  <span className={styles.checkboxText}>
                    Acepto los términos y condiciones. Y ser mayor de 18 años de edad.
                  </span>
                </Checkbox>
              </Form.Item>

              {registerButtonClicked && error && (
                <div className={styles.errorMessage}>
                  <Typography.Text style={{color: "var(--accent-danger)"}}>
                    {error?.response?.data?.message || error?.message || "Error en el registro"}
                  </Typography.Text>
                </div>
              )}

              <Form.Item style={{textAlign: "center", marginBottom: "20px"}}>
                <PrimaryButton
                  htmlType="submit"
                  disabled={!agreeToTerms}
                  loading={isPending}
                  className={styles.submitButton}
                >
                  {t("registrationAcceptTermsRegister")}
                </PrimaryButton>
              </Form.Item>

              <Form.Item style={{textAlign: "center", marginBottom: 0}}>
                <Typography.Text className={styles.loginText}>
                  ¿Ya tienes una cuenta?{" "}
                  <Typography.Link
                    className={styles.loginLink}
                    style={{color: "var(--action-primary)"}}
                    onClick={() => navigate("/mobile-login")}
                  >
                    Iniciar sesión
                  </Typography.Link>
                </Typography.Text>
              </Form.Item>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default MobileRegistration;
